import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Loader2, Save, Wrench } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import {
  fetchSystemSettings,
  updateSystemSettings,
  type AdminSystemSettings,
} from '@/lib/adapters'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog } from '@/components/ui/dialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A24.01 — Tizim sozlamalari. Only the config the app ACTUALLY reads
 * (app_settings via SettingsService): `maintenance_mode` (enforced by
 * middleware.Maintenance → 503 on user routes) + the live-stream numbers
 * consumed by LiveService. No fake toggles (grom-admin-design §5).
 * GET/PUT /admin/settings/system.
 */
export function SystemSettings() {
  const t = useT()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmMaint, setConfirmMaint] = useState(false)
  const [form, setForm] = useState<AdminSystemSettings | null>(null)

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true)
    setError(false)
    fetchSystemSettings(signal)
      .then((d) => setForm(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    load(ac.signal)
    return () => ac.abort()
  }, [load])

  const patch = (p: Partial<AdminSystemSettings>) =>
    setForm((f) => (f ? { ...f, ...p } : f))

  const save = useCallback(async () => {
    if (!form) return
    setSaving(true)
    try {
      await updateSystemSettings(form)
      toast(t.system.saved, { tone: 'success' })
    } catch {
      toast(t.system.saveError, { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }, [form, toast, t])

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t.system.title}
        screenId="A24.01"
        description={t.system.desc}
        verified
        actions={
          <Button onClick={save} disabled={saving || !form}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t.system.save}
          </Button>
        }
      />

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-secondary">
            <Loader2 size={15} className="animate-spin" /> {t.common.loading}
          </div>
        ) : error ? (
          <div className="flex flex-col items-start gap-2 text-sm text-danger">
            {t.system.loadError}
            <Button variant="ghost" onClick={() => load()}>
              {t.common.retry}
            </Button>
          </div>
        ) : form ? (
          <div className="mx-auto flex max-w-2xl flex-col gap-4">
            {/* Maintenance — a destructive, platform-wide switch. */}
            <section
              className={`rounded-md border p-3 ${
                form.maintenance_mode ? 'border-danger bg-danger/5' : 'border-border-default bg-surface-2'
              }`}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={form.maintenance_mode}
                  onChange={(e) => {
                    if (e.target.checked) setConfirmMaint(true)
                    else patch({ maintenance_mode: false })
                  }}
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-md font-medium text-primary">
                    <Wrench size={14} /> {t.system.maintenance}
                  </span>
                  <span className="mt-0.5 block text-sm text-secondary">{t.system.maintenanceDesc}</span>
                  {form.maintenance_mode && (
                    <span className="mt-2 flex items-center gap-1.5 text-xs font-medium text-danger">
                      <AlertTriangle size={13} /> {t.system.maintenanceWarn}
                    </span>
                  )}
                </span>
              </label>
            </section>

            {/* Live-stream config — real values consumed by LiveService. */}
            <section className="rounded-md border border-border-default bg-surface-2 p-3">
              <h2 className="mb-3 text-sm font-medium text-secondary">{t.system.liveSection}</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-sm text-secondary">{t.system.streamerPct}</span>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    className="font-mono"
                    value={String(form.live_gift_streamer_pct)}
                    onChange={(e) =>
                      patch({ live_gift_streamer_pct: Number(e.target.value) })
                    }
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-sm text-secondary">{t.system.maxCohosts}</span>
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    className="font-mono"
                    value={String(form.live_max_cohosts)}
                    onChange={(e) => patch({ live_max_cohosts: Number(e.target.value) })}
                  />
                </label>
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <Dialog
        open={confirmMaint}
        onClose={() => setConfirmMaint(false)}
        title={t.system.confirmMaintTitle}
      >
        <p className="text-sm text-secondary">{t.system.confirmMaint}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmMaint(false)}>
            {t.common.cancel}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              patch({ maintenance_mode: true })
              setConfirmMaint(false)
            }}
          >
            {t.system.confirmMaintOk}
          </Button>
        </div>
      </Dialog>
    </div>
  )
}
